// frontend/app/components/lessonPlans/lessonVideoSource.js
//
// Resolves a lesson's (source_video_id, tag_id) reference into a playable HLS
// URL and a start offset. Extracted so `LessonVideoPlayer` can own its own
// <video> element without going through `handleVideoClick`, which is bound to
// the Learning module's lifecycle (see LessonPwoPlayer.js for the same reason).
//
// The chain mirrors what the tile flow does:
//   1. GET  /video_metadata      -> uuid, version, tag_time, default_start_sec
//   2. POST /master_playlist     -> signed master + media playlist URLs
//   3. GET  /secure_master_m3u8  -> playlist URL (when the video has alternate
//      or /secure_m3u8              audio tracks; otherwise the simpler one)

import axios from "axios";

import { FASTAPI_BASE_URL } from "../../config";

//: Videos without an explicit default start skip an 11s title card when a tag
//: is being targeted. Matches normalizeVideoStartSec in AITutorApp.
const DEFAULT_TAG_PREROLL_SEC = 11;

export class LessonVideoSourceError extends Error {}

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/** `"MM:SS"` or `"HH:MM:SS"` -> seconds. */
export const parseTagTime = (tagTime) => {
  const parts = String(tagTime || "").split(":");
  if (!parts.length || !parts.every((part) => part.trim() !== "" && !Number.isNaN(Number(part)))) {
    return 0;
  }
  return parts.reduce((total, part) => total * 60 + Number(part), 0);
};

/**
 * As `parseTagTime`, but answers null instead of 0 when the value cannot be
 * read.
 *
 * The distinction only matters for the segment end, where it matters a lot: 0
 * would mean "stop at the very start" and the class would never see the clip.
 * The start has no such hazard, so `parseTagTime` keeps its forgiving contract.
 */
export const parseTagTimeOrNull = (tagTime) => {
  if (tagTime === null || tagTime === undefined) return null;
  const parts = String(tagTime).split(":");
  const readable =
    parts.length > 1 &&
    parts.every((part) => part.trim() !== "" && !Number.isNaN(Number(part)));
  return readable ? parseTagTime(tagTime) : null;
};

/**
 * Seconds at which the tagged segment stops, or null to play to the end.
 *
 * Null is a real answer with two causes that behave identically: this is the
 * last tag on the video, or the server could not say. Either way the video runs
 * on as it always did.
 */
export const resolveEndSec = ({ tagEndTime, startSec }) => {
  const endSec = parseTagTimeOrNull(tagEndTime);
  if (endSec === null) return null;
  // Never stop before we have started. If authored data puts the next tag at or
  // before this one, there is no segment to speak of, so play the video.
  return endSec > Number(startSec || 0) ? endSec : null;
};

/**
 * Floor the raw tag time by whichever minimum applies, so a tag at 00:00 does
 * not land the class on a title card.
 */
export const normalizeStartSec = ({
  rawSec,
  tagId,
  defaultStartSec = null,
  minimumStartSec = null,
}) => {
  const override = toNumberOrNull(minimumStartSec);
  const fallback = toNumberOrNull(defaultStartSec);

  const floor =
    override !== null
      ? override
      : fallback !== null
        ? fallback
        : tagId === null || tagId === undefined
          ? 0
          : DEFAULT_TAG_PREROLL_SEC;

  return Math.max(Number(rawSec) || 0, floor);
};

export async function resolveLessonVideoSource({
  videoId,
  tagId = null,
  languageId = 1,
  authToken,
  minimumStartSec = 0,
}) {
  if (!videoId) {
    throw new LessonVideoSourceError("This lesson block has no video reference.");
  }

  // ---- 1. metadata --------------------------------------------------------
  const params = { video_id: videoId, language_id: languageId };
  if (tagId !== null && tagId !== undefined && tagId !== "") {
    params.tag_id = tagId;
  }

  const metaResponse = await axios.get(`${FASTAPI_BASE_URL}/video_metadata`, { params });
  const metadata = metaResponse?.data;

  // `/video_metadata` answers 200 with `{ error: "..." }` for a video or tag it
  // cannot find, rather than a 4xx. Checking only the two sentinel strings let
  // that body through: `uuid_2D_720P` is then *undefined*, which equals neither
  // sentinel, so resolution carried on with an undefined uuid and failed two
  // steps later as "could not be prepared for playback" — a message pointing at
  // the wrong thing entirely.
  //
  // Reporting the server's own reason turns that into something diagnosable
  // from the screen instead of from the server log.
  if (!metadata) {
    throw new LessonVideoSourceError("This video is not available in the library.");
  }

  if (metadata.error) {
    throw new LessonVideoSourceError(`This video is not available: ${metadata.error}.`);
  }

  if (
    !metadata.uuid_2D_720P ||
    metadata.uuid_2D_720P === "Unknown" ||
    metadata.uuid_2D_720P === "Unknown Video" ||
    !metadata.version_2D_720P ||
    metadata.version_2D_720P === "Unknown"
  ) {
    throw new LessonVideoSourceError("This video is not available in the library.");
  }

  const videoUuid = metadata.uuid_2D_720P;
  const videoVersion = String(metadata.version_2D_720P);

  const startSec = normalizeStartSec({
    rawSec: parseTagTime(metadata.tag_time),
    tagId,
    defaultStartSec: metadata.default_start_sec,
    minimumStartSec,
  });

  // ---- 2. signed playlists ------------------------------------------------
  const authHeaders = { Authorization: `Bearer ${authToken}` };

  const masterResponse = await axios.post(
    `${FASTAPI_BASE_URL}/master_playlist`,
    { video_uuid: videoUuid, video_version: videoVersion },
    { headers: { ...authHeaders, "Content-Type": "application/json" } }
  );

  const masterData = masterResponse?.data || {};
  const mediaPlaylistUrl = masterData.signed_media_playlist_url;
  const masterUrl = masterData.signed_master_playlist_url;

  if (!mediaPlaylistUrl) {
    throw new LessonVideoSourceError("This video could not be prepared for playback.");
  }

  // ---- 3. secure playlist -------------------------------------------------
  const audioLines = masterData.audio_lines || "";
  const audioLinesText =
    typeof audioLines === "string" ? audioLines : JSON.stringify(audioLines);
  const hasAlternateAudio = audioLinesText.includes("TYPE=AUDIO");

  let playlistUrl = "";
  if (hasAlternateAudio) {
    const preferredAudio = Number(languageId) === 2 ? "guj" : "eng";
    const secureMaster = await axios.get(
      `${FASTAPI_BASE_URL}/secure_master_m3u8` +
        `?master_url=${encodeURIComponent(masterUrl)}` +
        `&video_uuid=${encodeURIComponent(videoUuid)}` +
        `&preferred_audio=${encodeURIComponent(preferredAudio)}`,
      { headers: authHeaders, responseType: "json" }
    );
    playlistUrl = secureMaster?.data?.playlist_url || "";
  } else {
    const securePlaylist = await axios.get(
      `${FASTAPI_BASE_URL}/secure_m3u8` +
        `?media_url=${encodeURIComponent(mediaPlaylistUrl)}` +
        `&video_uuid=${encodeURIComponent(videoUuid)}`,
      { headers: authHeaders, responseType: "json" }
    );
    playlistUrl = securePlaylist?.data?.playlist_url2 || "";
  }

  if (!playlistUrl) {
    throw new LessonVideoSourceError("No playable stream was returned for this video.");
  }

  return {
    playlistUrl,
    startSec,
    // null = play to the end of the video, which is what a lesson pointing at
    // the last tag of a recording actually wants.
    endSec: resolveEndSec({ tagEndTime: metadata.tag_end_time, startSec }),
    videoName: metadata.video_name || null,
    tagName: metadata.tag_name || null,
  };
}

export default resolveLessonVideoSource;
